import React, { useState, useCallback, type ChangeEvent, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import Form, { Field, ErrorMessage, RequiredAsterisk } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { shouldApplyPermissionsGate } from '@atlassian/jira-common-selectors/src/edition';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useDebouncedCallback } from '@atlassian/jira-platform-use-debounce';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';

import {
	FREE_ACCESS_LEVEL,
	OPEN_ACCESS_LEVEL,
	getApplicationForProject,
	getEdition,
} from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { DEBOUNCE_DELAY } from '../../common/constants';
import type { ImportType, SupportedThirdParty } from '../../common/constants/import-type';
import { X2C_SCREENS } from '../../common/constants/metrics';
import {
	PROJECT_NAME_END_LIMIT,
	PROJECT_NAME_START_LIMIT,
	ProjectStyles,
} from '../../common/constants/project';
import type { Template } from '../../common/constants/steps';

import { PageDescription, PageHeading } from '../../common/ui';
import { CloseAndReturnButton } from '../../common/ui/close-and-return-button';
import { StepFooter } from '../../common/ui/step-footer';
import { StepProgressBar } from '../../common/ui/step-progress-bar';
import { useContentForThirdParty } from '../../common/utils';
import { useProjectSpecificContent } from '../../common/utils/project-specific-content';
import { useProjectData, useThirdPartyState } from '../../controllers/import-state';
import { useSource } from '../../controllers/import-state/selectors/source';
import { useStartImportController } from '../../controllers/start-import';
import { useProductSteps } from '../../controllers/utils';
import { useGenerateProjectKey } from '../../services/generate-project-key';
import { useValidateProjectKey } from '../../services/validate-project-key';
import { useValidateProjectName } from '../../services/validate-project-name';
import { Illustration } from './illustration';
import { ProjectCreateScreenNew } from './main';
import messages from './messages';
import { ProjectCreateMoreOptions } from './project-create-more-options';
import { ProjectCreateStats } from './project-create-stats';
import { Templates } from './templates';

export type ProjectCreateScreenProps = {
	onReturn: () => void;
	onNext: () => void;
	onCloseAndReturnToUploadStep: () => void;
	hasProgressBar?: boolean;
	shouldPrefillThirdPartyProjectName?: boolean;
	shouldShowThirdPartyStats?: boolean;
	thirdParty?: SupportedThirdParty;
	shouldStartImportOnNext: boolean;
	isTMPSupported?: boolean;
	templates: Template[];
	importType?: ImportType;
};

export const ProjectCreateScreenOld = ({
	onReturn,
	onNext,
	hasProgressBar = false,
	shouldPrefillThirdPartyProjectName = false,
	shouldShowThirdPartyStats = false,
	thirdParty,
	shouldStartImportOnNext,
	isTMPSupported,
	onCloseAndReturnToUploadStep,
	templates,
}: ProjectCreateScreenProps) => {
	const { formatMessage } = useIntl();
	const { isLoading: isLoadingProjectNameValidation, validateProjectName } =
		useValidateProjectName();
	const { isLoading: isLoadingProjectKeyValidation, validateProjectKey } = useValidateProjectKey();
	const { isLoading: isLoadingProjectKey, generateProjectKey } = useGenerateProjectKey();
	const [projectData, { setProjectData }] = useProjectData();
	const [{ selectedSpaceName, selectedEntityName, selectedEntityStyle }] = useThirdPartyState();

	let DEFAULT_PROJECT_NAME = '';
	if (shouldPrefillThirdPartyProjectName && selectedEntityName) {
		if (selectedSpaceName) {
			DEFAULT_PROJECT_NAME = `${selectedSpaceName} - ${selectedEntityName}`;
		} else {
			DEFAULT_PROJECT_NAME = selectedEntityName;
		}
	}
	const [projectName, setProjectName] = useState<string>(projectData.name || DEFAULT_PROJECT_NAME);
	const [projectKey, setProjectKey] = useState<string>(projectData.key || '');
	const [projectNameValidationError, setProjectNameValidationError] = useState<string>('');
	const [projectKeyValidationError, setProjectKeyValidationError] = useState<string>('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { productSteps, currentStepIndex } = useProductSteps();
	const { startImport, isImportLoading, hasImportErrorOccurred, isStartImportDisabled } =
		useStartImportController({ thirdParty });
	const [source] = useSource();

	const { projectCreateHeading, projectCreateDescription } = useProjectSpecificContent(
		projectData.type,
		source,
	);

	const { projectCreateHeading: thirdPartyProjectCreateHeading } =
		useContentForThirdParty(thirdParty);

	const shouldDisableNext =
		!isSubmitting &&
		(projectNameValidationError !== '' ||
			projectKeyValidationError !== '' ||
			isLoadingProjectNameValidation ||
			isLoadingProjectKeyValidation ||
			isLoadingProjectKey);

	const shouldDisableImport = isStartImportDisabled || shouldDisableNext;
	const { appEditions } = useTenantContext();

	const isPermissionGated = useMemo(() => {
		if (isTMPSupported && projectData.type) {
			const application = getApplicationForProject(projectData.type);
			const edition = getEdition(application, appEditions);
			return shouldApplyPermissionsGate(application, edition);
		}
		return false;
	}, [appEditions, isTMPSupported, projectData.type]);
	const DEFAULT_PROJECT_ACCESS = isPermissionGated ? FREE_ACCESS_LEVEL : OPEN_ACCESS_LEVEL;

	useEffect(() => {
		if (!shouldPrefillThirdPartyProjectName || !projectName) {
			return;
		}

		validateProjectName(projectName).then((errorMessage) => {
			if (errorMessage) {
				setProjectNameValidationError(errorMessage);
				return;
			}
			setProjectNameValidationError('');
			generateProjectKey(projectName).then((generatedProjectKey) => {
				if (!isNull(generatedProjectKey)) {
					setProjectKey(generatedProjectKey);
					setProjectKeyValidationError('');
					setProjectData({ name: projectName, key: generatedProjectKey });
				}
			});
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [generateProjectKey]);

	useEffect(() => {
		// once project access is set, no changes should be made
		if (typeof projectData.accessLevel !== 'undefined') {
			return;
		}

		if (isTMPSupported && selectedEntityStyle !== ProjectStyles.COMPANY_MANAGED_PROJECT) {
			setProjectData({
				style: ProjectStyles.TEAM_MANAGED_PROJECT,
				accessLevel: DEFAULT_PROJECT_ACCESS,
			});
			return;
		}

		setProjectData({
			style: ProjectStyles.COMPANY_MANAGED_PROJECT,
			accessLevel: undefined,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [handleProjectNameChange] = useDebouncedCallback((newProjectName: string) => {
		const valueLength = newProjectName.trim().length;
		if (valueLength === 0) {
			setProjectNameValidationError(formatMessage(messages.projectNameEmpty));
			return;
		}
		if (valueLength < PROJECT_NAME_START_LIMIT) {
			setProjectNameValidationError(formatMessage(messages.projectNameTooShort));
			return;
		}
		if (valueLength > PROJECT_NAME_END_LIMIT) {
			setProjectNameValidationError(formatMessage(messages.projectNameTooLong));
			return;
		}

		validateProjectName(newProjectName).then((errorMessage) => {
			if (errorMessage) {
				setProjectNameValidationError(errorMessage);
				return;
			}
			setProjectNameValidationError('');
			generateProjectKey(newProjectName).then((generatedProjectKey) => {
				if (!isNull(generatedProjectKey)) {
					setProjectKey(generatedProjectKey);
					setProjectKeyValidationError('');
					setProjectData({ name: newProjectName, key: generatedProjectKey });
				}
			});
		});
	}, DEBOUNCE_DELAY);

	const onProjectNameChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setProjectName(e.target.value);
			setProjectNameValidationError('');
			handleProjectNameChange(e.target.value);
		},
		[setProjectName, handleProjectNameChange, setProjectNameValidationError],
	);

	const [handleProjectKeyChange] = useDebouncedCallback((newProjectKey: string) => {
		validateProjectKey(newProjectKey).then((errorMessage) => {
			if (errorMessage) {
				setProjectKeyValidationError(errorMessage);
				return;
			}
			setProjectKeyValidationError('');
			setProjectData({ key: newProjectKey });
		});
	}, DEBOUNCE_DELAY);

	const onProjectKeyChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const newKeyValue = e.target.value ? e.target.value.toUpperCase() : e.target.value;
			setProjectKey(newKeyValue);
			setProjectKeyValidationError('');
			handleProjectKeyChange(newKeyValue);
		},
		[handleProjectKeyChange, setProjectKey, setProjectKeyValidationError],
	);

	const onSubmit = useCallback(async () => {
		// Not possible since users would have to have loaded mapping csv in the previous screen
		// and submit button is disabled in this case
		if (shouldStartImportOnNext && shouldDisableImport) {
			return;
		}

		setIsSubmitting(true);

		const projectNameError = await validateProjectName(projectName);

		if (projectNameError) {
			setIsSubmitting(false);
			setProjectNameValidationError(projectNameError);
			return;
		}

		const projectKeyError = await validateProjectKey(projectKey);

		if (projectKeyError) {
			setIsSubmitting(false);
			setProjectKeyValidationError(projectKeyError);
			return;
		}

		if (!shouldStartImportOnNext) {
			onNext();
			return;
		}

		startImport({ projectData, onNext });
		setIsSubmitting(false);
	}, [
		shouldStartImportOnNext,
		shouldDisableImport,
		validateProjectName,
		projectName,
		validateProjectKey,
		projectKey,
		startImport,
		projectData,
		onNext,
	]);

	return (
		<ContextualAnalyticsData
			sourceName={X2C_SCREENS.SETUP_PROJECT}
			sourceType={SCREEN}
			attributes={{ thirdParty }}
		>
			<Wrapper data-testid="project-create-importer-upload-flow.ui.project-create-screen.wrapper">
				<CloseAndReturnButton onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep} />
				{hasProgressBar && (
					<StepProgressBar productSteps={productSteps} currentStepIndex={currentStepIndex} />
				)}
				<ContentWrapper>
					<CreateFormWrapper>
						<PageHeading>
							{thirdPartyProjectCreateHeading || formatMessage(projectCreateHeading)}
						</PageHeading>
						<PageDescription>
							{formatMessage(projectCreateDescription, {
								br: <br />,
							})}
							{ff('jira-business-to-software-project-imports') && <RequiredAsterisk />}
						</PageDescription>
						<Form onSubmit={onSubmit}>
							{({ formProps }) => (
								<form {...formProps}>
									<Field label={formatMessage(messages.projectName)} name="name" isRequired>
										{({ fieldProps }) => (
											<>
												<TextField
													testId="project-create-importer-upload-flow.ui.project-create-screen.project-name-field"
													{...fieldProps}
													value={projectName}
													onChange={onProjectNameChange}
													placeholder={formatMessage(messages.projectNamePlaceholder)}
												/>
												{projectNameValidationError !== '' && (
													<ErrorMessage testId="project-create-importer-upload-flow.ui.project-create-screen.project-name-error-message">
														{projectNameValidationError}
													</ErrorMessage>
												)}
											</>
										)}
									</Field>
									{templates.length !== 0 && <Templates templates={templates} />}
									{shouldShowThirdPartyStats && thirdParty && (
										<ProjectCreateStats thirdParty={thirdParty} />
									)}
									<ProjectCreateMoreOptions
										projectKey={projectKey}
										onProjectKeyChange={onProjectKeyChange}
										projectKeyValidationError={projectKeyValidationError}
										isTMPSupported={isTMPSupported}
										isPermissionGated={isPermissionGated}
										defaultProjectAccess={DEFAULT_PROJECT_ACCESS}
										disableProjectStyle={!isNil(selectedEntityStyle)}
									/>
									<FooterWrapper>
										<StepFooter
											onReturn={onReturn}
											shouldStartImportOnNext={shouldStartImportOnNext}
											isLoading={isImportLoading || isSubmitting}
											shouldDisableReturn={isImportLoading || isSubmitting}
											shouldDisableNext={
												shouldStartImportOnNext ? shouldDisableImport : shouldDisableNext
											}
											nextButtonType="submit"
											hasImportErrorOccurred={hasImportErrorOccurred}
										/>
									</FooterWrapper>
								</form>
							)}
						</Form>
					</CreateFormWrapper>
					<Box xcss={illustrationStyles}>
						<Illustration
							projectType={projectData.type}
							projectName={projectName}
							projectKey={projectKey}
							isTmp={projectData.style === ProjectStyles.TEAM_MANAGED_PROJECT}
						/>
					</Box>
				</ContentWrapper>
				<FireScreenAnalytics />
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

export const ProjectCreateScreen = (props: ProjectCreateScreenProps) => {
	if (fg('enable-x2c-standardised-flow')) {
		return <ProjectCreateScreenNew {...props} />;
	}

	return <ProjectCreateScreenOld {...props} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	height: '100%',
	width: '100%',
	boxSizing: 'border-box',
	padding: `${token('space.300', '24px')} ${token('space.1000', '80px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.input', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		width: 'fit-content',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexFlow: 'row',
	flexWrap: 'wrap',
	marginTop: token('space.1000', '80px'),
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CreateFormWrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	maxWidth: '528px',
	marginRight: token('space.600', '48px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapper = styled.div({
	display: 'flex',
	flexFlow: 'column',
	alignItems: 'end',
});

const illustrationStyles = xcss({
	marginLeft: token('space.600', '48px'),
});
